import { type MachineBase, type MachineStatus } from "@packsize/schemas/machines";

import { errorSeverity } from "@/constants";

export type MachineDisplayStatus = {
	status: MachineStatus;
	hasError: boolean;
	hasWarning: boolean;
};

export type MachineMessage = {
	message: string;
	severity: (typeof errorSeverity)[keyof typeof errorSeverity];
};

export const machineIsInErrorStatus = (machine: MachineBase): boolean => {
	return machine.status === "Error";
};

export const machineHasWarning = (machine: MachineBase): boolean => {
	return machine.workstations.some((ws) => ws.warnings.length > 0);
};

export const getMachineDisplayStatus = (machine: MachineBase): MachineDisplayStatus => {
	const hasError = machineIsInErrorStatus(machine);
	const hasWarning = machineHasWarning(machine);

	return {
		status: machine.status,
		hasError,
		hasWarning,
	};
};

export const getErrorStopReasonMachineMessage = (machine: MachineBase): MachineMessage | undefined => {
	if (!machineIsInErrorStatus(machine) || machine.workstations.length === 0) {
		return undefined;
	}

	let stopReason = machine.workstations[0].stopReason;
	if (!stopReason.isError && machine.workstations.length > 1) {
		if (machine.workstations[1].stopReason.isError) {
			stopReason = machine.workstations[1].stopReason;
		}
	}

	if ((stopReason.code ?? 0) <= 0 || !stopReason.isError) {
		return undefined;
	}

	return {
		message: getTranslationId(
			machine.machineType,
			errorSeverity.error,
			stopReason.code,
			stopReason.message,
			stopReason.arguments,
		),
		severity: errorSeverity.error,
	};
};

export const getWarningMachineMessages = (machine: MachineBase): MachineMessage[] => {
	return machine.workstations.flatMap((ws) =>
		ws.warnings.map((w) => {
			return {
				message: getTranslationId(machine.machineType, errorSeverity.warning, w.code, w.message, w.arguments),
				severity: errorSeverity.warning,
			};
		}),
	);
};

const getTranslationId = (
	machineType: string,
	severity: string,
	code: number,
	message: string,
	messageArguments: string[],
): string => {
	if (machineType === "X5") {
		const parameter = code === 1 && severity === errorSeverity.warning ? messageArguments[1] : undefined;
		return `${machineType} ${severity} ${code}${parameter !== undefined ? `-${parameter}` : ""}`;
	} else if (machineType === "X7") {
		return `${message !== "" ? message : "Unknown"}`;
	} else {
		return `${machineType} ${severity} ${code}`;
	}
};
